import { type AppState, Auth0Provider } from '@auth0/auth0-react';
import { AuthKitProvider } from '@workos-inc/authkit-react';

import type { ReactNode } from 'react';
import { AuthProvider as OidcProvider } from 'react-oidc-context';

import { AUTH, IS_OIDC_BUILD, IS_WORKOS_BUILD } from './config';
import { getUserManager } from './useOidcSession';
import { rememberWorkOSSession } from './useWorkOSSession';

interface AuthProviderProps {
  /**
   * Called once the IdP redirects back with a code; `returnTo` is whatever
   * `loginWithRedirect({ appState: { returnTo } })` asked to restore. It is
   * not integrity protected (AuthKit carries it as plain `state`), so the
   * callee must only navigate to a same-origin path.
   */
  onRedirectCallback: (returnTo?: string) => void;
  children: ReactNode;
}

/**
 * Mounts the SDK provider selected by `VITE_AUTH_PROVIDER`. Callers guard on
 * `AUTH_CONFIGURED` first; rendering this without config is a programming
 * error for `oidc` and a broken `Auth0Provider` for `auth0`.
 */
export function AuthProvider({ onRedirectCallback, children }: AuthProviderProps) {
  if (IS_WORKOS_BUILD) {
    return (
      <AuthKitProvider
        clientId={AUTH.clientId ?? ''}
        apiHostname={AUTH.apiHostname}
        // Studio signs in on the WorkOS-hosted domain for now, where AuthKit
        // cannot set its HttpOnly refresh cookie, so dev mode keeps the refresh
        // token in localStorage: the same posture as Auth0's
        // `cacheLocation="localstorage"`. Turn it off with a custom domain.
        devMode
        redirectUri={window.location.origin}
        onRefresh={rememberWorkOSSession}
        onRedirectCallback={({ state }) => {
          const returnTo = typeof state?.returnTo === 'string' ? state.returnTo : undefined;
          // AuthKit rewrites the address bar (dropping `code` and `state`, and
          // the router's history entry with them) right after this returns;
          // navigating afterwards keeps React Router's state intact.
          queueMicrotask(() => onRedirectCallback(returnTo));
        }}
      >
        {children}
      </AuthKitProvider>
    );
  }

  if (IS_OIDC_BUILD) {
    return (
      <OidcProvider
        userManager={getUserManager()}
        onSigninCallback={(user) => {
          const state = user?.state as { returnTo?: string } | undefined;
          onRedirectCallback(state?.returnTo);
        }}
      >
        {children}
      </OidcProvider>
    );
  }

  return (
    <Auth0Provider
      domain={AUTH.auth0Domain ?? ''}
      clientId={AUTH.clientId ?? ''}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: AUTH.audience,
        ...(AUTH.organization ? { organization: AUTH.organization } : {}),
      }}
      cacheLocation="localstorage"
      useRefreshTokens
      onRedirectCallback={(appState?: AppState) => onRedirectCallback(appState?.returnTo)}
    >
      {children}
    </Auth0Provider>
  );
}
